import { createSelector } from 'reselect';
import { format, isWithinRange, isBefore } from 'date-fns';
import { point, featureCollection } from '@turf/helpers';
import pointsInPolygon from '@turf/points-within-polygon';
import { parseDate } from '../modules/date-helpers';
import bbox from '@turf/bbox';
import bboxPolygon from '@turf/bbox-polygon';
import { getDateFromYYYYMMDD } from '../modules/date-helpers';

export const initialState = {
  from: '2017-10-01',
  to: format(new Date(), 'YYYY-MM-DD'),
  activeFrogType: undefined,
  lga: undefined,
  lgaGeo: undefined,
  location: undefined,
  submissions: [],
  activeSubmissions: [],
  frogsById: {},
  frogsByLatinName: {},
  submissionsFetched: false,
  showTable: false,
  tab: 'ALL',
  loading: true,
};
export const getInitialTo = tab =>
  tab === 'FIRST_YEAR' ? '2022-12-01' : initialState.to;
export const reducer = (state, action) => {
  console.debug('prev state:', state);
  console.debug('action:', action);
  switch (action.type) {
    case 'SET_FROM':
      return { ...state, from: action.payload || initialState.from };
    case 'SET_TO':
      return { ...state, to: action.payload || getInitialTo(state.tab) };
    case 'RESET_TO_INITIALSTATE':
      return {
        ...state,
        from: initialState.from,
        to: getInitialTo(state.tab),
        activeFrogType: undefined,
        lga: undefined,
        lgaGeo: undefined,
        location: undefined,
        submissionsFetched: false,
        showTable: false,
        loading: true,
        activeSubmissions: [],
      };
    case 'SET_TAB': {
      return {
        ...state,
        to: getInitialTo(action.payload),
        tab: action.payload,
      };
    }
    case 'SET_FROGS':
      const { frogsById, frogsByLatinName } = action.payload.reduce(
        (acc, val) => {
          acc.frogsById[val.id] = val;
          acc.frogsByLatinName[val.latin_name] = val;
          return acc;
        },
        { frogsById: {}, frogsByLatinName: {} }
      );
      return {
        ...state,
        frogsById,
        frogsByLatinName,
      };
    case 'SET_ACTIVE_FROG_TYPE': {
      return {
        ...state,
        activeFrogType: action.payload,
        activeSubmissions: [],
      };
    }
    case 'SET_LGA': {
      return {
        ...state,
        lga: action.payload,
        lgaGeo: undefined,
        activeFrogType: undefined,
      };
    }
    case 'SET_LGA_GEO': {
      return { ...state, lgaGeo: action.payload };
    }
    case 'SET_LOCATION': {
      return { ...state, location: action.payload };
    }
    case 'SET_ACTIVE_SUBMISSIONS': {
      return { ...state, activeSubmissions: action.payload };
    }

    case 'SET_SUBMISSIONS': {
      return {
        ...state,
        loading: false,
        submissions: action.payload.reduce((acc, val, i) => {
          if (val.validated_frog_ids) {
            // This handles ALL submission data set
            val.validated_frog_ids.forEach((x, i) => {
              acc.push({
                id: String(val.id) + '-' + String(i),
                lat: val.lat,
                lng: val.lng,
                frog_species_id: x,
                user_id: val.user_id || 1,
                date: new Date(val.captured_at),
                state: val.state,
              });
            });
          } else {
            // This handles first year confirmed frogs
            const frog = state.frogsByLatinName[val.scientificName];

            if (!frog) {
              console.log('missing from species:', val.scientificName);

              return acc;
            }
            acc.push({
              id: val.occurrenceID + i,
              lat: parseFloat(val.decimalLatitude),
              lng: parseFloat(val.decimalLongitude),
              frog_species_id: frog.id,
              user_id: val.recordedBy,
              date: parseDate(val.eventDate),
              state: val.stateProvince,
              obscured:
                val.geoprivacy === 'obscured' || val.geoprivacy === ' c',
            });
          }
          return acc;
        }, []),
        submissionsFetched: true,
      };
    }
    case 'SET_TABLE': {
      return { ...state, showTable: action.payload };
    }

    default:
      return state;
  }
};
export const resetToInitialState = date => ({ type: 'RESET_TO_INITIALSTATE' });
export const setTo = date => ({ type: 'SET_TO', payload: date });
export const setFrom = date => ({ type: 'SET_FROM', payload: date });
export const setActiveFrogType = frogType => ({
  type: 'SET_ACTIVE_FROG_TYPE',
  payload: frogType,
});
export const setSubmissions = submissions => ({
  type: 'SET_SUBMISSIONS',
  payload: submissions,
});
export const setFrogs = frogs => ({
  type: 'SET_FROGS',
  payload: frogs,
});
export const setLGA = lga => ({ type: 'SET_LGA', payload: lga });
export const setActiveSubmissions = x => ({
  type: 'SET_ACTIVE_SUBMISSIONS',
  payload: x,
});
export const setLGAGeo = lgaGeo => ({ type: 'SET_LGA_GEO', payload: lgaGeo });
export const setTab = tab => ({ type: 'SET_TAB', payload: tab });

export const setLocation = x => ({ type: 'SET_LOCATION', payload: x });
export const setShowTable = visible => ({
  type: 'SET_TABLE',
  payload: visible,
});

const selectFrom = s => s.from;
const selectTo = s => s.to;
const selectAllSubmissions = s => s.submissions;

export const selectShowTable = s => s.showTable;

export const selectSubmissions = createSelector(
  selectFrom,
  selectTo,
  s => s.lgaGeo,
  selectAllSubmissions,
  (fromString, toString, lgaGeo, submissions) => {
    const from = new Date(getDateFromYYYYMMDD(fromString));
    const to = new Date(getDateFromYYYYMMDD(toString));
    const lookup = {
      NSW: 'New South Wales',
      QLD: 'Queensland',
      SA: 'South Australia',
      TAS: 'Tasmania',
      WA: 'Western Australia',
      VIC: 'Victoria',
      ACT: 'Australian Capital Territory',
      NT: 'Northern Territory',
    };
    if (isBefore(to, from)) return [];

    const filteredWithGeo = xs => {
      if (!lgaGeo) {
        return xs;
      }
      const points = featureCollection(
        xs.reduce((acc, { lat, lng, ...rest }) => {
          const stateAbbreviationFromData = rest.state
            ? rest.state.toUpperCase()
            : undefined;
          const state = lookup[stateAbbreviationFromData];
          if (lgaGeo.features[0].properties.STE_NAME21 === state) {
            acc.push(point([lng, lat], rest));
          }
          return acc;
        }, [])
      );
      const lgaBboxPoly = bboxPolygon(bbox(lgaGeo.features[0]));
      const filteredBBOXPoints = pointsInPolygon(points, lgaBboxPoly);
      const pointsInLga = pointsInPolygon(filteredBBOXPoints, lgaGeo);
      const data = pointsInLga.features.map(x => ({
        lat: x.geometry.coordinates[1],
        lng: x.geometry.coordinates[0],
        frog_species_id: x.properties.frog_species_id,
        id: x.properties.id,
        user_id: x.properties.user_id || 1,
        date: x.properties.date,
        state: x.properties.state,
        obscured: x.obscured,
      }));
      return data;
    };

    return filteredWithGeo(submissions);
  }
);
const selectSubmissionsFilteredByFrog = createSelector(
  selectSubmissions,
  s => s.activeFrogType,
  (submissions, activeFrogType) => {
    if (!activeFrogType) return submissions;
    const frogTypeFilter = activeFrogType
      ? x => x.frog_species_id === activeFrogType
      : () => true;
    return submissions.filter(frogTypeFilter);
  }
);

const filterByTime = (from, to, tab, submissions) => {
  const initialTo = getInitialTo(tab);
  if (from === initialState.from && to === initialTo) return submissions;
  return submissions.filter(x => isWithinRange(x.date, from, to));
};
const selectSubmissionsFilteredByTime = createSelector(
  selectFrom,
  selectTo,
  s => s.tab,
  selectSubmissions,
  (from, to, tab, submissions) => filterByTime(from, to, tab, submissions)
);

export const selecSubmissionsAllFilters = createSelector(
  selectFrom,
  selectTo,
  s => s.tab,
  selectSubmissionsFilteredByFrog,
  (from, to, tab, submissions) => filterByTime(from, to, tab, submissions)
);

export const selectFrogsWithSubmissionCount = createSelector(
  selectSubmissionsFilteredByTime,
  s => s.frogsById,
  s => s.lgaGeo,
  (submissions, frogsById, lgaGeo) => {
    const submissionCountByFrogId = submissions.reduce((acc, val) => {
      const key = val['frog_species_id'];

      if (acc[key]) {
        acc[key] = acc[key] + 1;
        return acc;
      }
      acc[key] = 1;
      return acc;
    }, {});
    return Object.entries(frogsById)

      .filter(([id, frog]) => submissionCountByFrogId[id] > 0)
      .map(([id, frog]) => {
        return { ...frog, submissions: submissionCountByFrogId[id] };
      })
      .sort((a, b) => b.submissions - a.submissions);
  }
);
export const selectSubmissionAggregations = createSelector(
  selecSubmissionsAllFilters,
  submissions => {
    const { species, users } = submissions.reduce(
      (acc, val) => {
        acc.species.add(val.frog_species_id);
        acc.users.add(val.user_id);
        return acc;
      },
      {
        species: new Set(),
        users: new Set(),
      }
    );
    return {
      recordOfFrogs: submissions.length,
      uniqueSpecies: species.size,
      uniqueUsers: users.size,
    };
  }
);

export const selectSubmissionsWithMonthCount = createSelector(
  selectSubmissionsFilteredByFrog,
  submissions => {
    const keyed = submissions.reduce((acc, val) => {
      const key = format(val.date, 'YYYY-MMM') + '-' + val.date.getMonth();

      if (acc[key]) {
        acc[key] = acc[key] + 1;
        return acc;
      }
      acc[key] = 1;
      return acc;
    }, {});
    return Object.entries(keyed)
      .map(([key, count]) => {
        const [year, month, monthNum] = key.split('-');
        return { year, count, month, monthNum };
      })
      .sort((a, b) => {
        return new Date(a.year, a.monthNum) - new Date(b.year, b.monthNum);
      });
  }
);

export const selectSubmissionCountsByYear = createSelector(
  selectSubmissionsWithMonthCount,
  submissionsWithMonthCount => {
    const yearSplit = submissionsWithMonthCount.reduce((acc, n) => {
      const { year } = n;
      if (!acc[year]) {
        acc[year] = [n];
      } else {
        acc[year].push(n);
      }
      return acc;
    }, {});
    return Object.entries(yearSplit).map(([key, monthsArr]) => {
      return {
        year: key,
        months: monthsArr,
      };
    });
  }
);

export const selectActiveLGAName = s => {
  if (!s.lgaGeo) return undefined;
  return s.lgaGeo.features[0].properties.LGA_NAME21;
};
export const selectActiveFrogName = createSelector(
  s => s.activeFrogType,
  s => s.frogsById,
  (activeFrogType, frogsById) => {
    if (!activeFrogType) return undefined;
    return frogsById[activeFrogType].common_name;
  }
);
